import { useState, useEffect, Suspense, lazy } from "react";
import { ArrowUp, ShieldCheck } from "lucide-react";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import Header from "./components/Header";
import Hero from "./components/Hero";
import ReusableStats from "./components/Stats";
import Programs from "./components/Programs";
import Services from "./components/Services";
import Footer from "./components/Footer";
import { UmkmItem } from "./types";

// Lazy loading large/admin components and non-critical pages
const ProfilPage = lazy(() => import("./components/ProfilPage"));
const LayananPage = lazy(() => import("./components/LayananPage"));
const NewsCatalogPage = lazy(() => import("./components/NewsCatalogPage"));
const NewsDetailPage = lazy(() => import("./components/NewsDetailPage"));
const DkmPage = lazy(() => import("./components/DkmPage"));
const UmkmCatalogPage = lazy(() => import("./components/UmkmCatalogPage"));
const UmkmDetailPage = lazy(() => import("./components/UmkmDetailPage"));
const UmkmRegisterPage = lazy(() => import("./components/UmkmRegisterPage"));
const FormModal = lazy(() => import("./components/FormModal"));
const NewsAndDkm = lazy(() => import("./components/NewsAndDkm"));
const Umkm = lazy(() => import("./components/Umkm"));
const AdminLogin = lazy(() => import("./components/admin/AdminLogin"));
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

import { trackPageView } from "./lib/analytics";

export default function App() {
  // Modal configurations
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState<"surat" | "aspirasi" | "login" | "umkm" | null>(null);
  
  // Selected UMKM detail tracker
  const [selectedUmkm, setSelectedUmkm] = useState<UmkmItem | null>(null);

  // Simulated admin authority states
  const [adminName, setAdminName] = useState<string | null>(null);

  // Scroll to top button visibility state
  const [showScrollTop, setShowScrollTop] = useState(false);
  
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Scroll to top on route change
    window.scrollTo(0, 0);

    // Track page views in Firestore Analytics
    trackPageView(location.pathname);

    // Google Analytics page view tracking for all SPA pages/routes
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("config", "G-LF9T2V2QGR", {
        page_path: location.pathname + location.search,
      });
    }
  }, [location.pathname, location.search]);

  // Monitor scroll height to show back-to-top layout
  useEffect(() => {
    const checkScrollTop = () => {
      if (window.scrollY > 400) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };
    window.addEventListener("scroll", checkScrollTop);
    return () => window.removeEventListener("scroll", checkScrollTop);
  }, []);

  // Handle opening modal of generic types
  const handleOpenModal = (type: "surat" | "aspirasi" | "login" | "umkm" | "umkm-register") => {
    if (type === "surat") {
      navigate("/surat-pengantar");
    } else if (type === "aspirasi") {
      navigate("/aspirasi-warga");
    } else if (type === "umkm-register") {
      navigate("/umkm/daftar");
    } else {
      setModalType(type);
      setModalOpen(true);
    }
  };

  // Close and clean up modal values
  const handleCloseModal = () => {
    setModalOpen(false);
    setModalType(null);
  };

  // Handle simulated login success
  const handleLoginSuccess = (name: string) => {
    setAdminName(name);
    console.log(`💡 Selamat Datang Kembali! Anda berhasil masuk sebagai Administrator: ${name}.`);
  };

  // Log out admin state
  const handleLogout = () => {
    setAdminName(null);
    console.log("🔐 Sesi Administrator dicabut dengan aman.");
  };

  const handleScrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };

  const isHome = location.pathname === '/';
  const isAdminRoute = location.pathname.startsWith('/admin') || location.pathname === '/login-rw' || location.pathname === '/login';

  return (
    <div className="relative min-h-screen flex flex-col justify-between">
      
      {/* 1. TOPMOST EMERGENCY OR TICKER BAR (If Admin is Logged In) */}
      {adminName && !isAdminRoute && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-secondary text-primary font-bold px-4 py-2 text-center text-xs tracking-wider flex items-center justify-center gap-2 border-b border-yellow-400 shadow-md">
          <ShieldCheck className="w-5 h-5 text-primary stroke-[2.5]" />
          <span>Sesi Portal Utama Terbuka: Administrator Active ({adminName})</span>
          <button 
            onClick={handleLogout}
            className="ml-3 px-2 py-0.5 bg-primary text-white text-[10px] rounded hover:bg-primary-light transition-all cursor-pointer font-sans"
          >
            CABUT AKSES
          </button>
        </div>
      )}

      {/* 2. HEADER / NAVIGATION */}
      {!isAdminRoute && (
        <Header 
          onOpenModal={handleOpenModal} 
          adminName={adminName} 
          onLogout={handleLogout} 
        />
      )}

      {/* MAIN VIEW - ROUTED */}
      <main className={`flex-grow ${isHome ? "" : (isAdminRoute ? "" : "pt-24")}`}>
        <Suspense fallback={<div className="flex items-center justify-center min-h-screen text-slate-500">Memuat halaman...</div>}>
          <Routes>
            <Route path="/login-rw" element={<AdminLogin />} />
            <Route path="/login" element={<AdminLogin />} />
            <Route path="/admin/*" element={<AdminLayout />} />
            <Route path="/" element={
              <>
                <Hero onOpenModal={handleOpenModal} />
                <ReusableStats />
                <Programs />
                <Services onOpenModal={handleOpenModal} />
                <Suspense fallback={<div className="py-20 text-center text-slate-500">Memuat informasi...</div>}>
                  <NewsAndDkm />
                </Suspense>
                <Suspense fallback={<div className="py-20 text-center text-slate-500">Memuat UMKM...</div>}>
                  <Umkm 
                    onOpenModal={handleOpenModal} 
                    onSelectUmkm={setSelectedUmkm} 
                    onViewAll={() => navigate("/umkm")} 
                  />
                </Suspense>
              </>
            } />
            <Route path="/profil" element={<ProfilPage />} />
            <Route path="/layanan" element={<Services onOpenModal={handleOpenModal} />} />
            <Route path="/layanan/:id" element={<LayananPage />} />
            <Route path="/surat-pengantar" element={<LayananPage forcedType="surat" defaultTitle="Surat Pengantar RT/RW" />} />
            <Route path="/surat-domisili" element={<LayananPage forcedType="surat" defaultTitle="Surat Domisili" />} />
            <Route path="/surat-usaha" element={<LayananPage forcedType="surat" defaultTitle="Surat Keterangan Usaha" />} />
            <Route path="/aspirasi-warga" element={<LayananPage forcedType="aspirasi" defaultTitle="Infrastruktur & Jalan" />} />
            <Route path="/pengaduan" element={<LayananPage forcedType="aspirasi" defaultTitle="Keamanan & Ketertiban" />} />
            <Route path="/informasi" element={<NewsCatalogPage />} />
            <Route path="/informasi/:id" element={<NewsDetailPage />} />
            <Route path="/dkm" element={<DkmPage />} />
            <Route path="/umkm" element={<UmkmCatalogPage 
              onBack={() => {}} // Remove back behavior since it's a standalone page
              onOpenModal={handleOpenModal}
              onSelectUmkm={setSelectedUmkm}
            />} />
            <Route path="/umkm/daftar" element={<UmkmRegisterPage />} />
            <Route path="/umkm/:id" element={<UmkmDetailPage />} />
            {/* Fallback routes for galeri/kontak mapping to existing components for now, or you can map them appropriately */}
            <Route path="/galeri" element={<NewsAndDkm />} />
            <Route path="/kontak" element={<div className="py-20 text-center text-gray-500">Silakan lihat informasi kontak di bagian bawah halaman.</div>} />
          </Routes>
        </Suspense>
      </main>

      {/* 3. FOOTER COMPONENT */}
      {!isAdminRoute && (
        <Footer 
          onOpenModal={handleOpenModal} 
        />
      )}

      {/* 4. FLOATING BACK TO TOP BUTTON */}
      {showScrollTop && !isAdminRoute && (
        <button
          onClick={handleScrollToTop}
          className="fixed bottom-6 right-6 z-40 p-3 bg-accent hover:bg-accent-hover text-white rounded-full shadow-2xl hover:scale-110 transition-all cursor-pointer group"
          title="Kembali ke Atas"
          aria-label="Kembali ke Atas"
        >
          <ArrowUp className="w-5 h-5 group-hover:-translate-y-0.5 transition-transform" />
        </button>
      )}

      {/* 5. FLOATING FORM MODAL BACKEND WORKFLOW */}
      {!isAdminRoute && modalOpen && (
        <Suspense fallback={null}>
          <FormModal 
            isOpen={modalOpen} 
            onClose={handleCloseModal} 
            type={modalType} 
            selectedUmkm={selectedUmkm} 
            onLoginSuccess={handleLoginSuccess}
          />
        </Suspense>
      )}

    </div>
  );
}
